import { useLocation } from "react-router";
import { PodcastKeywordContext } from "@app/types/Page";
import Ad from "@components/Advertisement/Ad";
import AdSettings from "@components/Advertisement/AdSettings";
import Container from "@components/Container/Container";
import SeoFooter from "@components/Footer/SeoFooter/SeoFooter";
import GAData from "@components/GAData/GAData";
import Column from "@components/Grid/Column";
import Row from "@components/Grid/Row";
import Header from "@components/Header/Header";
import KeywordFilter from "@components/KeywordFilter";
import MetaTags from "@components/MetaTags/MetaTags";
import PulseEntry from "@features/pulse/components/PulseEntry/PulseEntry";
import { getUrlPathFromAbsoluteUrl } from "@pages/Article/utils/helpers";
import { getFormattedTextForAds } from "@pages/Section/helpers";
import { PODCASTS_KEYWORDS_DATA } from "@pages/Section/layouts/PodcastsPage/utils/constants";
import { Tag } from "@typings/Cue";
import { useDetectAdBlock } from "adblock-detect-react";

import PodcastsBanner from "./components/PodcastsBanner";
import PodcastsOverview from "./components/PodcastsOverview";

type PodcastKeywordProps = Pick<PodcastKeywordContext, "data">;

/**
 * Layout for Podcasts Keyword pages.
 * Figma: https://www.figma.com/design/rmzJoZwUpb3iLm6lMDXJ2W/BT-MASTER--WEB-?node-id=1476-9947&t=m7103yrVxON0jRlB-4
 */
const PodcastsKeywordLayout = ({
  data: { title, entity, overview, seoFooterData },
}: PodcastKeywordProps): React.ReactElement => {
  const adBlockDetected = useDetectAdBlock();
  const { pathname } = useLocation();

  const [parentCategory, childCategory] = pathname.replace("/", "").split("/");

  const {
    tag: { name, urlPath },
  } = entity;
  const termName = name || "";

  const relativePath = getUrlPathFromAbsoluteUrl(urlPath);
  const podcastKeyword = PODCASTS_KEYWORDS_DATA.find((data) => {
    return data.tag.urlPath === relativePath;
  });

  const legacyTag: Tag = {
    id: entity.tag.id,
    name: entity.tag.name,
    urlPath: entity.tag.urlPath,
    type: entity.tag.type,
    uri: "",
    urlPathHistory: entity.urlPathHistory,
    aliases: [],
    fields: [],
    parent: null,
    sections: [],
  };

  return (
    <div className="w-full" data-testid="Podcasts-keyword-content-component">
      <AdSettings
        adNames={["lb1", "imu2", "prestitial", "catfish"]}
        path="/keywords"
        adTargets={[
          { key: "page", value: "listing" },
          { key: "bttags", value: getFormattedTextForAds(termName) },
        ]}
      />

      <MetaTags
        title={title}
        description={`THE BUSINESS TIMES ${termName} - Find ${termName} News & Headlines, insight and analysis in Singapore, Asia-Pacific & global markets news at The Business Times.`}
        ogType="article"
        slug={urlPath}
      />

      <GAData
        chapter1={childCategory?.replaceAll("-", " ") || ""}
        level2={parentCategory?.replaceAll("-", " ") || ""}
        title={title}
        adblocker={adBlockDetected ? 1 : 0}
      />

      <Ad
        adType="lb1"
        className="flex min-h-[calc(50px+0.50rem)] flex-col items-center justify-center border-b border-gray-175 bg-gray-125 py-1 text-center md:py-3 lg:min-h-[calc(250px+1.5rem)]"
      />

      <Header />

      <PulseEntry />

      <Container rootClassName="py-8">
        <Row>
          <Column rootClassName="w-full mb-9 lg:mb-16">
            <>
              {legacyTag ? (
                <KeywordFilter
                  tag={legacyTag}
                  className="mb-6 inline-block w-max bg-gray-250 p-3"
                  buttonClassName="!ml-2"
                  rootClassName="bg-gray-250 p-3 inline-block mb-6"
                  tagClassName="flex-none"
                />
              ) : null}

              {podcastKeyword ? (
                <PodcastsBanner details={podcastKeyword} />
              ) : null}
            </>
          </Column>

          <Column rootClassName="w-full lg:w-8/12 mb-6">
            <p className="mb-0 border-t border-gray-850 pt-3 font-poppins font-bold uppercase tracking-[0.02em] lg:text-lg">
              episodes
            </p>

            <>
              {podcastKeyword && overview?.length > 0 ? (
                <PodcastsOverview cardProps={overview} entity={entity} />
              ) : null}
            </>
          </Column>

          <Column rootClassName="w-full mb-10 px-0 lg:px-3 lg:w-4/12">
            <Ad
              adType="imu2"
              className="flex flex-col items-center justify-center lg:!sticky lg:top-[70px]"
            />
          </Column>
        </Row>
      </Container>

      <SeoFooter data={seoFooterData} />

      <Ad adType="catfish" />

      <Ad adType="prestitial" />
    </div>
  );
};

export default PodcastsKeywordLayout;
